import {
  Group,
  Box3,
  Vector2,
  Vector3,
  Plane,
  Quaternion,
  Object3D,
  Raycaster,
  Mesh,
  CircleGeometry,
  MeshBasicMaterial,
  DoubleSide,
} from "three";
import Experience from "./Experience.js";
import { Chess as ChessRules } from "chess.js";
import gsap from "gsap";
import {
  CHESS_BASE_POSITION,
  CHESS_ROTATION_Y,
  CHESS_TARGET_WIDTH,
  CHESS_CAMERA_DISTANCE,
  CHESS_CAMERA_HEIGHT,
  CHESS_CAMERA_BACK,
} from "./constants.js";

const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"];
const PIECE_VALUE = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };
const TYPE_FROM_NAME = {
  Pawn: "p",
  Rook: "r",
  Knight: "n",
  Bishop: "b",
  Queen: "q",
  King: "k",
};

export default class Chess {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.resources = this.experience.resources;
    this.camera = this.experience.camera.instance;

    this.rules = new ChessRules(); // chess.js — source of truth for legality
    this.mode = "ai"; // "ai" | "hotseat"
    this.aiColor = "b"; // human plays White, AI plays Black
    this.aiDepth = 3;

    this.isActive = false;
    this.isAnimating = false;
    this.thinking = false;
    this.selected = null; // currently selected square (e.g. "e2")

    this.pointer = new Vector2();
    this.downPos = new Vector2();
    this.raycaster = new Raycaster();

    this.pieceNodes = []; // { node, color, type }
    this.nodeBySquare = {}; // "e2" -> Object3D
    this.squareCenters = {}; // "e2" -> Vector3 (world, board-top Y)
    this.queenTemplate = {}; // color -> Object3D to clone for promotion
    this.captured = []; // { node } for restore on new game
    this.promoted = []; // clones added for promotions

    this.setModel();
    this.deriveBoard();
    this.buildHighlights();
    this.setupHUD();
    this.setupDebug();
    this.updateStatus();
  }

  /* ----------------------------------------------------------------- model */

  setModel() {
    this.model = this.resources.items.chessSet.scene;
    this.model.name = "chessSet";
    this.model.isChess = true; // climbed by Navigation.checkIntersection

    // Reset transform, then auto-fit scale to CHESS_TARGET_WIDTH.
    this.model.position.set(0, 0, 0);
    this.model.rotation.set(0, 0, 0);
    this.model.scale.set(1, 1, 1);
    this.model.updateMatrixWorld(true);

    this.boardNode = null;
    this.model.traverse((c) => {
      if (/ChessBoard/i.test(c.name) && !this.boardNode) this.boardNode = c;
    });
    const naturalBox = new Box3().setFromObject(this.boardNode || this.model);
    const naturalSize = new Vector3();
    naturalBox.getSize(naturalSize);
    const naturalWidth = Math.max(naturalSize.x, naturalSize.z) || 1;
    this.scaleFactor = CHESS_TARGET_WIDTH / naturalWidth;

    this.model.scale.setScalar(this.scaleFactor);
    this.model.rotation.y = CHESS_ROTATION_Y;
    this.model.updateMatrixWorld(true);

    const placedBox = new Box3().setFromObject(this.boardNode || this.model);
    this.boardWorldWidth = Math.max(
      placedBox.max.x - placedBox.min.x,
      placedBox.max.z - placedBox.min.z
    );

    // Place the board's bottom-centre exactly on the pouf top.
    // CHESS_BASE_POSITION is the verified WORLD position of the old baked board
    // (the room model has a node transform, so this is in the transformed/world
    // frame the runtime actually uses), set directly — no surface raycast.
    this.placeAt(
      CHESS_BASE_POSITION.x,
      CHESS_BASE_POSITION.y,
      CHESS_BASE_POSITION.z
    );

    this.scene.add(this.model);
  }

  // Baked room meshes used as the "floor/furniture" the set rests on.
  surfaceTargets() {
    const baked = this.experience.world && this.experience.world.baked;
    const t = [];
    if (baked && baked.model) {
      ["room1", "room2", "room3"].forEach((k) => {
        if (baked.model[k]) t.push(baked.model[k]);
      });
    }
    return t;
  }

  // Move the set so the board's bottom-centre sits exactly at world (x, y, z).
  placeAt(x, y, z) {
    this.model.updateMatrixWorld(true);
    const box = new Box3().setFromObject(this.boardNode || this.model);
    const bottom = new Vector3(
      (box.min.x + box.max.x) / 2,
      box.min.y,
      (box.min.z + box.max.z) / 2
    );
    this.model.position.add(new Vector3(x, y, z).sub(bottom));
    this.model.updateMatrixWorld(true);
  }

  /* Build the square<->world map and initial piece assignment purely from the
     model's own geometry (the two pawn rows define the grid), so it stays
     correct no matter how the set is scaled or rotated. */
  deriveBoard() {
    const pieces = [];
    this.model.traverse((node) => {
      const m = node.name && node.name.match(/^(White|Black)(Pawn|Rook|Knight|Bishop|Queen|King)/);
      if (!m) return;
      const color = m[1] === "White" ? "w" : "b";
      const type = TYPE_FROM_NAME[m[2]];
      const world = new Vector3();
      node.getWorldPosition(world);
      node.userData.restY = world.y;
      node.userData.color = color;
      node.userData.type = type;
      const entry = { node, color, type, world };
      pieces.push(entry);
      this.pieceNodes.push(entry);
      if (type === "q" && !this.queenTemplate[color]) {
        this.queenTemplate[color] = node;
      }
    });

    const centroid = (arr) => {
      const c = new Vector3();
      arr.forEach((p) => c.add(p.world));
      return c.multiplyScalar(1 / arr.length);
    };
    const whitePawns = pieces.filter((p) => p.color === "w" && p.type === "p");
    const blackPawns = pieces.filter((p) => p.color === "b" && p.type === "p");
    const Cw = centroid(whitePawns); // white pawns = rank index 1
    const Cb = centroid(blackPawns); // black pawns = rank index 6

    // rank axis (white -> black), 5 squares between the two pawn rows
    const rankStep = Cb.clone().sub(Cw).multiplyScalar(1 / 5);
    rankStep.y = 0;
    // file axis = horizontal perpendicular to rank axis
    const fileAxis = new Vector3(-rankStep.z, 0, rankStep.x).normalize();
    const proj = (p) => p.world.dot(fileAxis);
    const sortedW = [...whitePawns].sort((a, b) => proj(a) - proj(b));
    const fileSpan = proj(sortedW[7]) - proj(sortedW[0]);
    const fileStepLen = fileSpan / 7;
    const fileStep = fileAxis.clone().multiplyScalar(fileStepLen);
    this.squareSize = Math.max(fileStepLen, rankStep.length());

    const boardBox = new Box3().setFromObject(this.boardNode || this.model);
    this.boardTopY = boardBox.max.y;
    const boardCentre = new Vector3();
    boardBox.getCenter(boardCentre);
    this.boardCentre = new Vector3(boardCentre.x, this.boardTopY, boardCentre.z);

    // square (file i 0..7, rank j 0..7) centre, white back rank = j 0
    const centreFor = (i, j) => {
      const base = Cw.clone()
        .add(rankStep.clone().multiplyScalar(j - 1))
        .add(fileStep.clone().multiplyScalar(i - 3.5));
      base.y = this.boardTopY;
      return base;
    };

    // Orientation: find white king/queen geometric file index to fix a..h.
    const nearestIJ = (world) => {
      let best = null,
        bestD = Infinity;
      for (let i = 0; i < 8; i++)
        for (let j = 0; j < 8; j++) {
          const c = centreFor(i, j);
          const d = (c.x - world.x) ** 2 + (c.z - world.z) ** 2;
          if (d < bestD) {
            bestD = d;
            best = { i, j };
          }
        }
      return best;
    };
    const wKing = pieces.find((p) => p.color === "w" && p.type === "k");
    const wQueen = pieces.find((p) => p.color === "w" && p.type === "q");
    const kingI = nearestIJ(wKing.world).i;
    const queenI = nearestIJ(wQueen.world).i;
    const flip = kingI < queenI; // king must end on a higher file (e) than queen (d)
    const fileLetter = (i) => FILES[flip ? 7 - i : i];

    // square centres for all 64 squares
    for (let i = 0; i < 8; i++)
      for (let j = 0; j < 8; j++) {
        const sq = fileLetter(i) + (j + 1);
        this.squareCenters[sq] = centreFor(i, j);
      }

    // assign every piece to its starting square via nearest centre
    this.nodeBySquare = {};
    pieces.forEach((p) => {
      const { i, j } = nearestIJ(p.world);
      const sq = fileLetter(i) + (j + 1);
      this.nodeBySquare[sq] = p.node;
      p.node.userData.square = sq;
    });

    // sanity log against chess.js initial board
    let mismatch = 0;
    this.rules.board().forEach((row, r) => {
      row.forEach((cell) => {
        if (!cell) return;
        const node = this.nodeBySquare[cell.square];
        if (
          !node ||
          node.userData.color !== cell.color ||
          node.userData.type !== cell.type
        )
          mismatch++;
      });
    });
    if (mismatch) console.warn(`[Chess] ${mismatch} initial square mismatches`);
  }

  buildHighlights() {
    this.highlightGroup = new Group();
    this.highlightGroup.name = "chessHighlights";
    this.scene.add(this.highlightGroup);

    const r = this.squareSize * 0.42;
    const disc = (color, opacity) =>
      new Mesh(
        new CircleGeometry(r, 24),
        new MeshBasicMaterial({
          color,
          transparent: true,
          opacity,
          depthWrite: false,
          side: DoubleSide,
        })
      );

    this.selectMarker = disc(0xffd54a, 0.85);
    this.selectMarker.rotation.x = -Math.PI / 2;
    this.selectMarker.visible = false;
    this.highlightGroup.add(this.selectMarker);

    this.moveMarkers = [];
    for (let i = 0; i < 32; i++) {
      const d = disc(0x4ad6ff, 0.7);
      d.rotation.x = -Math.PI / 2;
      d.visible = false;
      this.moveMarkers.push(d);
      this.highlightGroup.add(d);
    }
  }

  /* ------------------------------------------------------------------- HUD */

  setupHUD() {
    this.ui = document.getElementById("chess-ui");
    this.statusEl = document.getElementById("chess-status");
    this.modeBtn = document.getElementById("chess-mode");
    this.resetBtn = document.getElementById("chess-reset");

    if (this.modeBtn)
      this.modeBtn.addEventListener("click", () => {
        this.mode = this.mode === "ai" ? "hotseat" : "ai";
        this.updateStatus();
        if (this.mode === "ai") this.maybeAI();
      });
    if (this.resetBtn)
      this.resetBtn.addEventListener("click", () => this.newGame());
  }

  updateStatus() {
    if (this.modeBtn)
      this.modeBtn.textContent =
        this.mode === "ai" ? "Mode: vs Computer" : "Mode: 2 Players";
    if (!this.statusEl) return;
    let msg;
    if (this.rules.isCheckmate())
      msg = (this.rules.turn() === "w" ? "Black" : "White") + " wins — checkmate";
    else if (this.rules.isDraw()) msg = "Draw";
    else if (this.rules.isStalemate()) msg = "Stalemate";
    else {
      const side = this.rules.turn() === "w" ? "White" : "Black";
      msg =
        (this.mode === "ai" && this.rules.turn() === this.aiColor
          ? "Computer thinking…"
          : side + " to move") + (this.rules.inCheck() ? " — check" : "");
    }
    this.statusEl.textContent = msg;
  }

  /* -------------------------------------------------------------- controls */

  activateControls() {
    if (this.ui) this.ui.classList.add("show-chess-ui");
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointerup", this.onPointerUp);
    this.isActive = true;
    this.updateStatus();
    this.maybeAI();
  }

  deactivateControls() {
    if (this.ui) this.ui.classList.remove("show-chess-ui");
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointerup", this.onPointerUp);
    this.isActive = false;
    this.clearSelection();
  }

  onPointerMove = (e) => {
    this.pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
  };

  onPointerDown = (e) => {
    this.downPos.set(
      (e.clientX / window.innerWidth) * 2 - 1,
      -(e.clientY / window.innerHeight) * 2 + 1
    );
  };

  onPointerUp = (e) => {
    // Ignore clicks on UI overlays (HUD buttons, back button, banner) so they
    // don't get interpreted as a move on the board behind them.
    if (
      e.target &&
      e.target.closest &&
      e.target.closest("button, a, .chess-ui, .banner, .circular-button")
    )
      return;
    const up = new Vector2(
      (e.clientX / window.innerWidth) * 2 - 1,
      -(e.clientY / window.innerHeight) * 2 + 1
    );
    if (up.distanceTo(this.downPos) > 0.01) return; // a drag, not a click
    if (this.isAnimating || this.thinking) return;
    if (this.mode === "ai" && this.rules.turn() === this.aiColor) return;
    const sq = this.squareAtPointer();
    if (sq) this.handleClick(sq);
  };

  squareAtPointer() {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const plane = new Plane(new Vector3(0, 1, 0), -this.boardTopY);
    const hit = new Vector3();
    if (!this.raycaster.ray.intersectPlane(plane, hit)) return null;
    let best = null,
      bestD = Infinity;
    for (const sq in this.squareCenters) {
      const c = this.squareCenters[sq];
      const d = (c.x - hit.x) ** 2 + (c.z - hit.z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = sq;
      }
    }
    return Math.sqrt(bestD) < this.squareSize * 0.6 ? best : null;
  }

  /* ------------------------------------------------------------- selection */

  handleClick(sq) {
    const turn = this.rules.turn();
    const piece = this.rules.get(sq);

    if (this.selected) {
      const legal = this.rules
        .moves({ square: this.selected, verbose: true })
        .find((m) => m.to === sq);
      if (legal) {
        this.doMove({ from: this.selected, to: sq, promotion: "q" });
        return;
      }
      if (piece && piece.color === turn) {
        this.selectSquare(sq); // switch selection
        return;
      }
      this.clearSelection();
      return;
    }
    if (piece && piece.color === turn) this.selectSquare(sq);
  }

  selectSquare(sq) {
    this.selected = sq;
    const c = this.squareCenters[sq];
    this.selectMarker.position.set(c.x, this.boardTopY + 0.002, c.z);
    this.selectMarker.visible = true;

    const moves = this.rules.moves({ square: sq, verbose: true });
    this.moveMarkers.forEach((m, i) => {
      if (i < moves.length) {
        const t = this.squareCenters[moves[i].to];
        m.position.set(t.x, this.boardTopY + 0.002, t.z);
        m.visible = true;
      } else m.visible = false;
    });
  }

  clearSelection() {
    this.selected = null;
    if (this.selectMarker) this.selectMarker.visible = false;
    if (this.moveMarkers) this.moveMarkers.forEach((m) => (m.visible = false));
  }

  /* ------------------------------------------------------------------ move */

  doMove({ from, to, promotion }) {
    const move = this.rules.move({ from, to, promotion });
    if (!move) return;
    this.clearSelection();
    this.isAnimating = true;
    this.updateStatus();

    const node = this.nodeBySquare[from];

    // capture (incl. en passant)
    if (move.captured) {
      const capSq =
        move.flags.indexOf("e") !== -1 ? move.to[0] + from[1] : move.to;
      const capNode = this.nodeBySquare[capSq];
      if (capNode) {
        delete this.nodeBySquare[capSq];
        this.capturePiece(capNode);
      }
    }

    // castling — move the rook too
    if (move.flags.indexOf("k") !== -1 || move.flags.indexOf("q") !== -1) {
      const rank = from[1];
      const kingSide = move.flags.indexOf("k") !== -1;
      const rookFrom = (kingSide ? "h" : "a") + rank;
      const rookTo = (kingSide ? "f" : "d") + rank;
      const rookNode = this.nodeBySquare[rookFrom];
      if (rookNode) {
        delete this.nodeBySquare[rookFrom];
        this.nodeBySquare[rookTo] = rookNode;
        rookNode.userData.square = rookTo;
        this.placeNodeAtSquare(rookNode, rookTo, true);
      }
    }

    // move the piece
    delete this.nodeBySquare[from];
    this.placeNodeAtSquare(node, to, true, () => {
      // promotion — swap pawn for a cloned queen
      if (move.flags.indexOf("p") !== -1) {
        node.visible = false;
        const clone = this.queenTemplate[move.color].clone(true);
        clone.visible = true;
        node.parent.add(clone);
        this.promoted.push({ clone, pawn: node });
        clone.userData.restY = node.userData.restY;
        clone.userData.color = move.color;
        clone.userData.type = "q";
        this.nodeBySquare[to] = clone;
        clone.userData.square = to;
        this.placeNodeAtSquare(clone, to, false);
      } else {
        this.nodeBySquare[to] = node;
        node.userData.square = to;
      }
      this.isAnimating = false;
      this.updateStatus();
      this.maybeAI();
    });
  }

  placeNodeAtSquare(node, sq, animate, onComplete) {
    const c = this.squareCenters[sq];
    const parent = node.parent;
    parent.updateWorldMatrix(true, false);
    const worldTarget = new Vector3(c.x, node.userData.restY, c.z);
    const local = parent.worldToLocal(worldTarget.clone());
    if (!animate) {
      node.position.copy(local);
      if (onComplete) onComplete();
      return;
    }
    // small hop, expressed in local units (scale-safe)
    const hopWorld = this.squareSize * 0.6;
    const localUp = parent.worldToLocal(
      worldTarget.clone().add(new Vector3(0, hopWorld, 0))
    );
    const hop = localUp.y - local.y;
    const tl = gsap.timeline({ onComplete });
    tl.to(node.position, { x: local.x, z: local.z, duration: 0.55, ease: "power2.inOut" }, 0);
    tl.to(node.position, { y: local.y + hop, duration: 0.27, ease: "power2.out" }, 0);
    tl.to(node.position, { y: local.y, duration: 0.28, ease: "power2.in" }, 0.27);
  }

  capturePiece(node) {
    this.captured.push({ node, square: node.userData.square });
    const parent = node.parent;
    parent.updateWorldMatrix(true, false);
    // shove it off the board edge, then hide
    const off = this.boardCentre
      .clone()
      .add(new Vector3(this.squareSize * 5.2, node.userData.restY - this.boardCentre.y, 0));
    const local = parent.worldToLocal(off);
    gsap.to(node.position, {
      x: local.x,
      z: local.z,
      duration: 0.5,
      ease: "power2.in",
      onComplete: () => (node.visible = false),
    });
  }

  /* -------------------------------------------------------------------- AI */

  maybeAI() {
    if (!this.isActive) return;
    if (this.mode !== "ai") return;
    if (this.rules.isGameOver()) return;
    if (this.rules.turn() !== this.aiColor) return;
    this.thinking = true;
    this.updateStatus();
    setTimeout(() => {
      let best = null;
      try {
        best = this.searchBestMove(this.aiDepth);
      } catch (e) {
        best = null;
      }
      if (!best) {
        const moves = this.rules.moves({ verbose: true });
        best = moves[Math.floor(Math.random() * moves.length)];
      }
      this.thinking = false;
      if (best) this.doMove({ from: best.from, to: best.to, promotion: "q" });
    }, 420);
  }

  searchBestMove(depth) {
    this.nodeBudget = 80000;
    const moves = this.orderedMoves();
    let best = null;
    let alpha = -Infinity;
    for (const m of moves) {
      this.rules.move(m);
      const score = -this.negamax(depth - 1, -Infinity, -alpha);
      this.rules.undo();
      if (score > alpha || best === null) {
        alpha = score;
        best = m;
      }
    }
    return best;
  }

  negamax(depth, alpha, beta) {
    if (this.nodeBudget-- <= 0) return this.evaluate();
    if (this.rules.isGameOver()) {
      if (this.rules.isCheckmate()) return -100000 - depth; // prefer faster mate
      return 0; // draw / stalemate
    }
    if (depth <= 0) return this.evaluate();
    const moves = this.orderedMoves();
    for (const m of moves) {
      this.rules.move(m);
      const score = -this.negamax(depth - 1, -beta, -alpha);
      this.rules.undo();
      if (score >= beta) return beta;
      if (score > alpha) alpha = score;
    }
    return alpha;
  }

  orderedMoves() {
    // captures first (MVV-LVA-ish) for better alpha-beta pruning
    return this.rules.moves({ verbose: true }).sort((a, b) => {
      const av = a.captured ? PIECE_VALUE[a.captured] - PIECE_VALUE[a.piece] : -1;
      const bv = b.captured ? PIECE_VALUE[b.captured] - PIECE_VALUE[b.piece] : -1;
      return bv - av;
    });
  }

  evaluate() {
    // material + a little central control, from side-to-move's perspective
    let score = 0;
    const board = this.rules.board();
    for (let r = 0; r < 8; r++)
      for (let f = 0; f < 8; f++) {
        const cell = board[r][f];
        if (!cell) continue;
        let v = PIECE_VALUE[cell.type];
        const centre = 3.5;
        v += (3 - (Math.abs(f - centre) + Math.abs(r - centre))) * 2;
        score += cell.color === "w" ? v : -v;
      }
    return this.rules.turn() === "w" ? score : -score;
  }

  /* -------------------------------------------------------------- new game */

  newGame() {
    this.rules.reset();
    this.clearSelection();
    this.promoted.forEach(({ clone, pawn }) => {
      if (clone.parent) clone.parent.remove(clone);
      pawn.visible = true;
    });
    this.promoted = [];
    this.captured.forEach(({ node }) => (node.visible = true));
    this.captured = [];
    // put every piece back on its home square
    this.nodeBySquare = {};
    this.pieceNodes.forEach(({ node }) => {
      const home = node.userData.homeSquare;
      if (home) {
        this.nodeBySquare[home] = node;
        node.userData.square = home;
        this.placeNodeAtSquare(node, home, false);
      }
    });
    this.isAnimating = false;
    this.thinking = false;
    this.updateStatus();
    this.maybeAI();
  }

  /* ----------------------------------------------------------- camera view */

  getCameraView() {
    const back = this.whiteBackDir();
    const w = this.boardWorldWidth || CHESS_TARGET_WIDTH;
    const backMul = this._camBack != null ? this._camBack : CHESS_CAMERA_BACK;
    const heightMul =
      this._camHeight != null ? this._camHeight : CHESS_CAMERA_HEIGHT;
    // Aim slightly above the board surface so the camera frames the pieces.
    const look = this.boardCentre
      .clone()
      .add(new Vector3(0, w * 0.25, 0));
    const position = this.boardCentre
      .clone()
      .add(back.clone().multiplyScalar(w * backMul))
      .add(new Vector3(0, w * heightMul, 0));
    const dummy = new Object3D();
    dummy.position.copy(position);
    dummy.up.set(0, 1, 0);
    dummy.lookAt(look);
    const quaternion = new Quaternion().copy(dummy.quaternion);
    return { position, quaternion, target: look };
  }

  whiteBackDir() {
    // direction pointing from the board centre toward the White player's side
    const a = this.squareCenters["d1"];
    const b = this.squareCenters["d8"];
    const dir = a.clone().sub(b);
    dir.y = 0;
    return dir.normalize();
  }

  /* ------------------------------------------------------------- debug pane */

  setupDebug() {
    // remember home squares for new-game restore
    for (const sq in this.nodeBySquare)
      this.nodeBySquare[sq].userData.homeSquare = sq;

    if (typeof window === "undefined") return;
    // Dependency-free live placement helper. In the browser console:
    //   window.chessPlace()                      -> prints current values
    //   window.chessPlace(x, y, z, rotY, scale)  -> moves the set live
    // Copy the printed values into CHESS_BASE_POSITION / CHESS_ROTATION_Y in
    // constants.js once it looks right.
    window.chessPlace = (x, y, z, rotY, scale) => {
      if (x === undefined) {
        const v = {
          x: this.model.position.x,
          y: this.model.position.y,
          z: this.model.position.z,
          rotY: this.model.rotation.y,
          scale: this.model.scale.x,
        };
        console.log("[Chess] current placement", v);
        return v;
      }
      this.model.position.set(x, y, z);
      if (rotY !== undefined) this.model.rotation.y = rotY;
      if (scale !== undefined) this.model.scale.setScalar(scale);
      this.model.updateMatrixWorld(true);
    };
    // Nudge tool: move the set to world (x, z), keeping it at pouf-top height.
    // Watch the screen and call until it's centred, then copy x/z into constants.
    window.chessSnap = (x, z, y) => {
      this.placeAt(x, y != null ? y : CHESS_BASE_POSITION.y, z);
      console.log("[Chess] moved to", { x, z });
    };
    // Live camera-framing tuner. window.chessCam(back, height) in board-widths;
    // exit and click CHESS again to re-frame. No args prints current values.
    window.chessCam = (back, height) => {
      if (back === undefined) {
        const v = {
          back: this._camBack != null ? this._camBack : CHESS_CAMERA_BACK,
          height: this._camHeight != null ? this._camHeight : CHESS_CAMERA_HEIGHT,
        };
        console.log("[Chess] camera", v);
        return v;
      }
      this._camBack = back;
      if (height !== undefined) this._camHeight = height;
      console.log("[Chess] camera updated — exit and click CHESS to re-frame.");
    };
    // One-click placement: load the page with #chessplace in the URL, then
    // SHIFT-CLICK the pouf (or the old board) in the room. The set jumps to the
    // exact clicked point and logs the coords to bake into CHESS_BASE_POSITION.
    if (window.location.hash.toLowerCase().includes("chessplace")) {
      window.addEventListener("pointerdown", (e) => {
        if (!e.shiftKey) return;
        const nav = this.experience.navigation;
        if (nav && nav.currentStage !== null) return;
        const ndc = new Vector2(
          (e.clientX / window.innerWidth) * 2 - 1,
          -(e.clientY / window.innerHeight) * 2 + 1
        );
        const ray = new Raycaster();
        ray.setFromCamera(ndc, this.camera);
        const hits = ray.intersectObjects(this.surfaceTargets(), true);
        if (!hits.length) return;
        const p = hits[0].point;
        this.placeAt(p.x, p.y, p.z);
        console.log("[Chess] placed — set CHESS_BASE_POSITION to:", {
          x: +p.x.toFixed(3),
          y: +p.y.toFixed(3),
          z: +p.z.toFixed(3),
        });
      });
    }
  }

  update() {
    // gentle pulse on the legal-move markers while a piece is selected
    if (!this.isActive || !this.selected) return;
    const s = 1 + Math.sin(performance.now() * 0.005) * 0.12;
    this.moveMarkers.forEach((m) => {
      if (m.visible) m.scale.setScalar(s);
    });
  }
}
